import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  MenuItem,
  Snackbar,
  SnackbarContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { axiosInstance } from "common/network";

// Which Mandrill templates this account may send through
// POST /v3/accounts/notifications -- for the services that send email as one
// of our accounts (the popul8 demo, first).
//
// One scope per template, notifications.template.<slug>, registered from
// Mandrill by api-email-sender and closed by default. Opened here either for
// the whole account, or for one of its applications -- the one holding a
// service's API key -- so the account's other credentials stay closed. An
// application's own choice decides before the account's; an account Blocked
// stays blocked whatever its applications say (docs/portal-feature-gates.md in
// api-accounts).
//
// The whole card is one request (settings_availability), and a change is the
// change plus that request again: never a request per template or application.

const PREFIX = "notifications.template.";
const API = process.env.REACT_APP_API_ROOT;

const ACCOUNT_OPTIONS = [
  { value: "inherit", label: "Inherit global default" },
  { value: "available", label: "Available" },
  { value: "blocked", label: "Blocked" },
];
const APPLICATION_OPTIONS = [
  { value: "inherit", label: "Its account decides" },
  { value: "available", label: "Available" },
  { value: "blocked", label: "Blocked" },
];
const toChoice = (adminAvailable) =>
  adminAvailable === true ? "available" : adminAvailable === false ? "blocked" : "inherit";
const fromChoice = (choice) =>
  choice === "available" ? true : choice === "blocked" ? false : null;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "white",
    marginTop: "28px",
  },
  content: {
    padding: theme.spacing(3),
  },
  template: {
    fontWeight: 600,
  },
  description: {
    color: "rgba(0,0,0,0.6)",
    marginBottom: theme.spacing(1),
  },
  gate: {
    minWidth: 220,
  },
  effective: {
    color: "rgba(0,0,0,0.6)",
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  empty: {
    color: "rgba(0,0,0,0.6)",
  },
  loading: {
    padding: theme.spacing(3),
    display: "flex",
    justifyContent: "center",
  },
  snackSuccess: {
    backgroundColor: "#2e7d32",
  },
  snackError: {
    backgroundColor: "#c62828",
  },
}));

const TemplatePermissions = ({ accountUuid, scope, applicationNames, saving, onChange }) => {
  const classes = useStyles();
  const rows = [
    {
      key: "account",
      label: "The whole account",
      url: `${API}v3/accounts/${accountUuid}/settings/${scope.scope}/admin-availability`,
      options: ACCOUNT_OPTIONS,
      gate: scope.account,
    },
    ...scope.applications.map((gate) => ({
      key: gate.applicationUuid,
      label: applicationNames[gate.applicationUuid] || gate.applicationUuid,
      url: `${API}v3/applications/${gate.applicationUuid}/settings/${scope.scope}/admin-availability`,
      options: APPLICATION_OPTIONS,
      gate,
    })),
  ];

  return (
    <div>
      <Typography variant="subtitle1" className={classes.template}>
        {scope.scope.slice(PREFIX.length)}
      </Typography>
      <Typography variant="body2" className={classes.description}>
        {scope.description}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>For</TableCell>
            <TableCell>Permission</TableCell>
            <TableCell>In force</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>{row.label}</TableCell>
              <TableCell>
                <TextField
                  select
                  size="small"
                  className={classes.gate}
                  value={toChoice(row.gate.adminAvailable)}
                  disabled={saving}
                  onChange={(e) => onChange(row.url, e.target.value)}
                >
                  {row.options.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell className={classes.effective}>
                {row.gate.available ? "May send" : "May not send"}
                {row.gate.reason !== "Ok" ? ` (${row.gate.reason})` : ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

TemplatePermissions.propTypes = {
  accountUuid: PropTypes.string.isRequired,
  scope: PropTypes.object.isRequired,
  applicationNames: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const EmailTemplatePermissions = ({ accountUuid }) => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const response = await axiosInstance.get(`${API}v3/accounts/${accountUuid}/settings_availability`, {
        params: { prefix: PREFIX },
      });
      const applications = response?.data?.applications || [];
      setData({
        scopes: (response?.data?.scopes || []).filter((scope) => scope.active),
        applicationNames: applications.reduce((names, app) => ({ ...names, [app.uuid]: app.name || app.uuid }), {}),
      });
    } catch (err) {
      console.log("EmailTemplatePermissions:", err?.response?.status, err);
      setError(true);
    }
  }, [accountUuid]);

  useEffect(() => {
    if (accountUuid) load();
  }, [accountUuid, load]);

  const change = async (url, choice) => {
    setSaving(true);
    try {
      await axiosInstance.put(url, { available: fromChoice(choice) });
      setSnack({ severity: "success", message: "Permission updated." });
    } catch (err) {
      console.log("EmailTemplatePermissions change:", err);
      setSnack({ severity: "error", message: "Could not update the permission." });
    }
    await load(); // the server's truth, success or not
    setSaving(false);
  };

  if (!accountUuid) return null;
  return (
    <Card className={classes.root}>
      <CardHeader
        title="Email templates"
        subheader="Which Mandrill templates this account, or one of its applications, may send through the accounts API"
      />
      <Divider />
      <CardContent className={classes.content}>
        {error ? (
          <Typography className={classes.empty}>
            Could not load the email templates.{" "}
            <Button size="small" color="primary" onClick={load}>
              Try again
            </Button>
          </Typography>
        ) : !data ? (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        ) : data.scopes.length === 0 ? (
          <Typography className={classes.empty}>
            No email templates are registered yet. api-email-sender registers one for every Mandrill template.
          </Typography>
        ) : (
          data.scopes.map((scope, i) => (
            <React.Fragment key={scope.scope}>
              {i > 0 && <Divider className={classes.divider} />}
              <TemplatePermissions
                accountUuid={accountUuid}
                scope={scope}
                applicationNames={data.applicationNames}
                saving={saving}
                onChange={change}
              />
            </React.Fragment>
          ))
        )}
      </CardContent>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          className={snack?.severity === "error" ? classes.snackError : classes.snackSuccess}
          message={snack?.message || ""}
        />
      </Snackbar>
    </Card>
  );
};

EmailTemplatePermissions.propTypes = {
  accountUuid: PropTypes.string,
};

export default EmailTemplatePermissions;
